import { cn } from "app/lib/utils"
import type { PlayerControlSize } from "app/player/controlSizes"
import { getPlayerThickIcon } from "app/player/getPlayerThickIcon"
import { ComponentProps } from "react"
import PlayerIcon from "./PlayerIcon"
import PlayerIconButton from "./PlayerIconButton"
import usePlayToggleControls, {
  type PlayToggleState,
} from "./usePlayToggleControls"

const PLAY_TOGGLE_STATES = ["play", "pause", "replay"] as const

const ICON_FILES: Record<
  PlayToggleState,
  { file: string; size: number; className?: string; active?: boolean }
> = {
  play: {
    file: "ico_play.svg",
    size: 18,
    className: "ml-[1px]",
  },
  pause: { file: "ico_pause.svg", size: 18 },
  replay: {
    file: "ico_replay.svg",
    size: 20,
    className: "rotate-180",
  },
}

/** Largest icon size — keeps the button content box stable across states. */
const ICON_BOX_SIZE = ICON_FILES.replay.size

type Props = Omit<ComponentProps<typeof PlayerIconButton>, "children"> & {
  onReplayClick?: ComponentProps<typeof PlayerIconButton>["onClick"]
  size?: PlayerControlSize
}

export default function VideoSquarePlayButton({
  onReplayClick,
  onClick,
  onMouseDown,
  onMouseLeave,
  className,
  size = "md",
  color = "blue",
  ...props
}: Props) {
  const { state, buttonProps } = usePlayToggleControls({
    onClick,
    onReplayClick,
    onMouseDown,
    onMouseLeave,
    className,
  })
  const iconColor = color ?? "blue"

  return (
    <PlayerIconButton size={size} color={color} {...props} {...buttonProps}>
      <span
        className="relative inline-flex shrink-0 items-center justify-center"
        style={{ width: ICON_BOX_SIZE, height: ICON_BOX_SIZE }}
      >
        {/* Keep all icons mounted so toggling does not wait on a fresh <img> load. */}
        {PLAY_TOGGLE_STATES.map((key) => {
          const icon = ICON_FILES[key]
          const isActive = state === key
          return (
            <PlayerIcon
              key={key}
              src={getPlayerThickIcon(iconColor, icon.file)}
              size={icon.size}
              className={cn(
                "absolute",
                icon.className,
                !isActive && "invisible"
              )}
            />
          )
        })}
      </span>
    </PlayerIconButton>
  )
}
